import React, { useRef, useMemo, useState, useCallback, useEffect } from 'react';
import { observer } from 'mobx-react';

import {
    ShoppingCartOutlined as ShoppingCart,
    ClockCircleOutlined as Clock,
    InfoCircleOutlined as Info,
    MinusOutlined as Minus,
    PlusOutlined as Plus,
    FileTextOutlined,
    InboxOutlined,
    DownOutlined as ChevronDown,
    RightOutlined as ChevronRight,
    SearchOutlined as Search,
    CloseOutlined as X,
} from '@ant-design/icons';
import { Divider, Card, Form, notification, message } from 'antd';
import { QuoteProps, IRequirement, QuoteLineItemProps } from '@partsbadger/types';
import { FormErrorParser } from '@partsbadger/library';
import { CalculatingPriceState } from '@partsbadger/common-features';
import QuoteContactForm from './QuoteContactForm';
import QuoteActionButtons from './QuoteActionButtons';
import QuoteTermsCheckbox from './QuoteTermsCheckbox';
import QuotePricingSummary from './QuotePricingSummary';
import { DeliveryAndRequirementsSection } from './DeliveryAndRequirementsSection';
import { useHistory } from 'react-router-dom';
import PartItem from './PartItem';
import _ from 'lodash';
import QuoteStore from '@/stores/QuoteStore';
import UserStore from '@/stores/UserStore';
import { isPhoneNumberValid } from '@/utils/utils';
import {
    CUSTOM_OPTION_REQUIRED_MESSAGE,
    isAnyCustomOptionMissing,
    isConfiguredPartComplete,
} from '../customOptionValidation';
import { isAnyLineItemAnalyzing } from '../lineItemAnalysis';

interface IOnRequestQuotePayload {
    [key: string]: unknown;
}

type CheckoutMode = 'portal' | 'rapid_public';

interface IQuoteSidebarProps {
    quote: QuoteProps;
    onRequestQuote: (payload: IOnRequestQuotePayload, quote_uid?: string) => void;
    onUpdateQuoteField: (field: string, value: unknown) => void;
    onUpdateQuoteFields: (payload: Record<string, unknown>) => void;
    loading: boolean;
    isUserLoggedIn: boolean;
    additionalRequirements: IRequirement[];
    onSaveDraft: () => void;
    onGetHelp: () => void;
    zipCode: string;
    estimatedTax: number;
    areAllItemsSafeToAutoQuote: boolean;
    sortedMasterProducts: QuoteLineItemProps[][];
    checkoutMode?: CheckoutMode;
    onGuestCheckout?: () => void;
    onRequestModalLoggedIn: (open: boolean) => void;
    isStaffUser: boolean;
    hasPendingCustomOption?: boolean;
}

interface HandleSubmitParams {
    markAsCompleted?: boolean;
    force_all_to_review?: boolean;
    quote_uid?: string;
}

const PERSONAL_EMAIL_DOMAINS = new Set([
    'gmail.com',
    'hotmail.com',
    'outlook.com',
    'live.com',
    'yahoo.com',
    'icloud.com',
    'me.com',
    'msn.com',
    'aol.com',
    'protonmail.com',
    'proton.me',
]);

const isPersonalEmailDomain = (email?: string | null) => {
    if (!email) return false;

    const domain = email.toLowerCase().trim().split('@')[1];
    if (!domain) return false;

    return PERSONAL_EMAIL_DOMAINS.has(domain);
};

const trimStr = (v: unknown) => (v == null ? '' : String(v)).trim();

/** Contact.account may be a CRM account object ({ name }) or a legacy string. */
const formatAccountLabel = (account: unknown): string => {
    if (account == null || account === '') return '';
    if (typeof account === 'string') return trimStr(account);
    if (typeof account === 'object' && account !== null && 'name' in account) {
        return trimStr((account as { name?: unknown }).name);
    }
    return '';
};

const QuoteSideBar: React.FC<IQuoteSidebarProps> = ({
    quote,
    onRequestQuote,
    onUpdateQuoteField,
    onUpdateQuoteFields,
    loading,
    isUserLoggedIn,
    additionalRequirements,
    onSaveDraft,
    onGetHelp,
    zipCode,
    estimatedTax,
    areAllItemsSafeToAutoQuote,
    sortedMasterProducts,
    onRequestModalLoggedIn,
    isStaffUser,
    hasPendingCustomOption = false,
}) => {
    const [disableSubmit, setDisableSubmit] = useState(false);
    const [form] = Form.useForm();
    const isRequote = quote?.type === 'Requote';
    const guestEmail = quote?.email || quote?.contact?.email || UserStore.guest_data?.email || '';

    const backendIsHobbyist = quote?.contact?.is_hobbyist === true;
    const fallbackIsHobbyist = !!guestEmail && isPersonalEmailDomain(guestEmail);

    const isHobbyist = backendIsHobbyist || fallbackIsHobbyist;
    const showDelivery = !isHobbyist || isRequote;
    const history = useHistory();

    const completeParts = useMemo(() => quote?.line_items?.filter(part => isConfiguredPartComplete(part)) ?? [], [
        quote?.line_items,
    ]);

    const getQuoteActionTotals = useMemo(() => {
        const items = quote?.line_items || [];
        return items.reduce(
            (acc, item) => {
                if (item.quote_action === 'buy_now') {
                    acc.buy_now += 1;
                } else if (item.quote_action === 'manual_quote') {
                    acc.manual_quote += 1;
                }
                return acc;
            },
            { buy_now: 0, manual_quote: 0 }
        );
    }, [quote?.line_items]);

    const showTermsAndConditions = true;
    const quoteAccount = quote?.account;

    type Source = 'contact' | 'quote' | 'user' | 'none';

    const getFirstNameMeta = (): { value: string; source: Source } => {
        if (quote?.contact?.first_name) {
            return { value: trimStr(quote.contact.first_name), source: 'contact' };
        }

        if (quote?.first_name) {
            return { value: trimStr(quote.first_name), source: 'quote' };
        }

        if (isUserLoggedIn && UserStore.user?.first_name) {
            return { value: trimStr(UserStore.user.first_name), source: 'user' };
        }

        return { value: '', source: 'none' };
    };

    const firstNameMeta = getFirstNameMeta();

    const shouldHideFirstName =
        (firstNameMeta.source === 'contact' || firstNameMeta.source === 'quote') && firstNameMeta.value !== '';

    const getPhone = () => {
        if (quote?.contact) {
            return trimStr(quote.contact.phone) || trimStr(quote.phone);
        }
        return trimStr(quote?.phone);
    };

    const getFirstName = () => {
        if (quote?.contact) {
            return trimStr(quote.contact.first_name) || trimStr(quote.first_name);
        }
        if (isUserLoggedIn && UserStore.user) {
            return trimStr(quote?.first_name) || trimStr(UserStore.user.first_name);
        }
        return trimStr(quote?.first_name);
    };

    const getLastName = () => {
        if (quote?.contact) {
            return trimStr(quote.contact.last_name) || trimStr(quote.last_name);
        }
        if (isUserLoggedIn && UserStore.user) {
            return trimStr(quote?.last_name) || trimStr(UserStore.user.last_name);
        }
        return trimStr(quote?.last_name);
    };

    const getCompany = () => {
        if (quote?.contact) {
            return trimStr(quote.company) || formatAccountLabel(quote.contact.account);
        }
        if (isUserLoggedIn && UserStore.user) {
            return trimStr(quote?.company) || trimStr(UserStore.user.company);
        }
        return trimStr(quote?.company);
    };
    const totals = useMemo(() => {
        const items = quote?.line_items || [];

        const subtotal = items.reduce((acc, item) => {
            // Require safe_auto_quote so a stale buy_now/needs_manual_quote pair
            // cannot add an instant price to the total after brep-explorer failed
            // (PTB-6278).
            if (item.safe_auto_quote && item.quote_action === 'buy_now' && !item.needs_manual_quote) {
                return acc + (item.total_price ? parseFloat(item.total_price) || 0 : 0);
            }
            return acc;
        }, 0);

        const taxRate = parseFloat(quote?.taxes_rate || '0') || 0;
        const taxTotal = Number((subtotal * (taxRate / 100)).toFixed(2));
        const grandTotal = Number((subtotal + taxTotal).toFixed(2));

        return {
            subtotal,
            taxTotal,
            grandTotal,
        };
    }, [quote?.line_items, estimatedTax, quote?.taxes_rate]);

    const handleSubmitRFQ = async ({
        markAsCompleted = false,
        force_all_to_review,
        quote_uid,
    }: HandleSubmitParams = {}) => {
        form.validateFields().then(async values => {
            const customRequirements = form.getFieldValue('custom_requirements_local') ?? [];

            const payload: IOnRequestQuotePayload = {
                ...values,
                first_name: trimStr(values.first_name) || getFirstName(),
                last_name: trimStr(values.last_name) || getLastName(),
                company: trimStr(values.company) || getCompany(),
                phone: trimStr(values.phone) || getPhone(),
                shipping_zip_code: trimStr(values.shipping_zip_code) || zipCode,
                markAsCompleted,
                custom_requirements: customRequirements,
            };

            if (force_all_to_review) {
                payload.force_all_to_review = force_all_to_review;
            }

            onRequestQuote(payload, quote_uid);
        });
    };

    const areAllFilesValid = () => {
        for (const item of quote?.line_items || []) {
            const product = item?.product;

            /* -----------------------------
             * Material validation
             * ----------------------------- */
            if (!product?.material) {
                notification.error({
                    key: 'form-errors',
                    duration: 8,
                    message: 'Material Required',
                    description: `Please select a material for '${item.name}' to continue.`,
                    placement: 'topRight',
                    maxCount: 1,
                });
                return false;
            }

            if (product.material.includes('Review 2D') && !product.has_file_2d) {
                notification.error({
                    key: 'form-errors',
                    duration: 8,
                    message: 'Material Required',
                    description: `The selected material for '${item.name}' requires a 2D drawing. Please upload a 2D file to continue.`,
                    placement: 'topRight',
                    maxCount: 1,
                });
                return false;
            }

            /* -----------------------------
             * Hobbyist rule (3D required)
             * ----------------------------- */
            if (isHobbyist && !product.has_file_3d) {
                notification.error({
                    key: 'form-errors',
                    duration: 8,
                    message: '3D File Required',
                    description: `A 3D file is required for '${item.name}'. Please upload a 3D model to continue.`,
                    placement: 'topRight',
                    maxCount: 1,
                });
                return false;
            }
        }

        return true;
    };

    const areAllCustomOptionsValid = () => {
        if (hasPendingCustomOption || quote?.line_items?.some(item => isAnyCustomOptionMissing(item))) {
            message.error(CUSTOM_OPTION_REQUIRED_MESSAGE);
            return false;
        }

        return true;
    };

    const isSafeToCompleteQuote = (quote && areAllItemsSafeToAutoQuote) ?? false;
    const hasMixedQuoteItems = getQuoteActionTotals?.buy_now > 0 && getQuoteActionTotals?.manual_quote > 0;

    const isAnalyzing = isAnyLineItemAnalyzing(quote?.line_items);

    const defaultAdditionalFiles = useMemo(() => {
        return (
            quote?.additional_files?.map((file: { filename: string; id: number }) => {
                return {
                    id: file.id,
                    uid: file.id.toString(),
                    name: file.filename,
                    size: 0,
                    type: '',
                };
            }) ?? []
        );
    }, [quote?.additional_files]);

    const debouncersRef = useRef(new Map<string, _.DebouncedFunc<(value: unknown) => void>>());

    const debouncedUpdateField = useCallback(
        (field: string, value: unknown) => {
            if (!debouncersRef.current.has(field)) {
                const isZipField = field === 'shipping_zip_code';
                debouncersRef.current.set(
                    field,
                    _.debounce(
                        (v: unknown) => {
                            onUpdateQuoteField(field, v);
                            if (isZipField) setDisableSubmit(false);
                        },
                        1800,
                        { maxWait: 5000 }
                    )
                );
            }
            if (field === 'shipping_zip_code') {
                setDisableSubmit(true);
            }
            const debouncer = debouncersRef.current.get(field);
            debouncer?.(value);
        },
        [onUpdateQuoteField]
    );
    return (
        <>
            <Card
                title={
                    <div className="pb-1">
                        <h3
                            data-testid="quote-summary-title"
                            className="flex items-center space-x-2 text-slate-900"
                            style={{
                                fontSize: '1.5rem',
                                fontWeight: 600,
                                lineHeight: '1',
                                letterSpacing: '-0.025em',
                            }}
                        >
                            <FileTextOutlined className="w-5 h-5" style={{ fontSize: '18px' }} />
                            <span>Quote Summary</span>
                        </h3>
                    </div>
                }
                className="shadow-lg border-0 bg-white/80 backdrop-blur-md"
                headStyle={{}}
                bodyStyle={{ padding: '0 20px' }}
            >
                <Form
                    form={form}
                    colon={false}
                    initialValues={{
                        first_name: getFirstName(),
                        last_name: getLastName(),
                        company: getCompany(),
                        phone: getPhone(),
                        shipping_zip_code: zipCode,
                        // accepted_terms_conditions: false,
                        additional_files: defaultAdditionalFiles,
                    }}
                    onValuesChange={changedValues => {
                        if ('first_name' in changedValues) {
                            const first_name = changedValues.first_name;
                            if (first_name && first_name.trim() !== '') {
                                debouncedUpdateField('first_name', first_name);
                            }
                        }
                        if ('last_name' in changedValues) {
                            const last_name = changedValues.last_name;
                            if (last_name && last_name.trim() !== '') {
                                debouncedUpdateField('last_name', last_name);
                            }
                        }
                        if ('company' in changedValues) {
                            const company = changedValues.company;
                            if (company && company.trim() !== '') {
                                debouncedUpdateField('company', company);
                            }
                        }

                        // and in zip change handling:
                        if ('shipping_zip_code' in changedValues) {
                            const zip = (changedValues.shipping_zip_code ?? '').toString();
                            // optional: only send when user likely finished
                            if (zip.trim().length >= 5) debouncedUpdateField('shipping_zip_code', zip);
                        }

                        if ('phone' in changedValues) {
                            const currentPhoneRaw = changedValues.phone;
                            const currentPhone = (currentPhoneRaw ?? '').toString();

                            if (currentPhone === getPhone()) return;

                            if (currentPhone.trim() === '') {
                                debouncedUpdateField('phone', '');
                                return;
                            }

                            if (isPhoneNumberValid(currentPhone, false)) {
                                debouncedUpdateField('phone', currentPhone);
                            }
                        }
                    }}
                    layout="vertical"
                    scrollToFirstError={true}
                    className={'checkout'}
                >
                    <div className="">
                        {/* Parts List */}
                        <div className="space-y-3">
                            <h4 className="font-medium text-slate-900 flex items-center space-x-2">
                                <InboxOutlined className="w-4 h-4" />
                                <span>Parts</span>
                            </h4>
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                                {quote?.line_items?.length > 0 ? (
                                    sortedMasterProducts?.map(line_items => {
                                        return line_items?.map((part, index) => {
                                            const isComplete = isConfiguredPartComplete(part);
                                            const isVariation = index >= 1;
                                            const variantName = isVariation ? `Variant #${index}` : null;

                                            return (
                                                <PartItem
                                                    key={part.id}
                                                    part={part}
                                                    isComplete={isComplete}
                                                    variantName={variantName}
                                                />
                                            );
                                        });
                                    })
                                ) : (
                                    <div className="text-center py-6">
                                        <InboxOutlined className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                                        <p className="text-sm text-slate-500">No parts added yet</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {showDelivery && (
                            <>
                                <Divider />
                                <DeliveryAndRequirementsSection
                                    quote={quote}
                                    isRequote={isRequote}
                                    handleUpdateQuoteField={onUpdateQuoteField}
                                    handleUpdateQuoteFields={onUpdateQuoteFields}
                                    additionalRequirements={additionalRequirements}
                                    isStaffUser={isStaffUser}
                                />
                            </>
                        )}
                        <Divider />
                        <QuoteContactForm
                            firstName={getFirstName()}
                            lastName={getLastName()}
                            isHobbyist={isHobbyist}
                            hasAccount={!!quoteAccount}
                            shouldHideNames={shouldHideFirstName}
                        />
                        {/* Pricing Summary (subtotal only) */}
                        {isAnalyzing ? (
                            <CalculatingPriceState className="py-2" />
                        ) : totals.grandTotal ? (
                            <QuotePricingSummary
                                subTotalQuote={totals.subtotal}
                                taxTotalQuote={totals.taxTotal}
                                grandTotalQuote={totals.grandTotal}
                                completePartsLength={completeParts.length}
                            />
                        ) : null}
                        <div>
                            <QuoteTermsCheckbox showTerms={showTermsAndConditions} />
                        </div>
                        <QuoteActionButtons
                            isSafeToCompleteQuote={isSafeToCompleteQuote}
                            loading={loading}
                            disableSubmit={disableSubmit}
                            isAnalyzing={isAnalyzing}
                            setSubmitAndMarkAsCompleted={action => {
                                form.validateFields()
                                    .then(() => {
                                        if (!areAllFilesValid()) {
                                            return false;
                                        }

                                        if (!areAllCustomOptionsValid()) {
                                            return false;
                                        }

                                        if (action === 'checkout') {
                                            if (!isUserLoggedIn) {
                                                notification.warning({
                                                    key: 'login-to-checkout',
                                                    duration: 3,
                                                    message: 'Login Required',
                                                    description:
                                                        'Please login or create an account to proceed to checkout.',
                                                    placement: 'top',
                                                    maxCount: 1,
                                                });
                                            }

                                            history.push(`/my-account/quotes/${quote?.uid}/checkout/`);
                                        }

                                        if (action === 'complete') {
                                            // works good
                                            //if all items are safe the user can only complete the quote withut need to login
                                            handleSubmitRFQ({
                                                markAsCompleted: true,
                                            });
                                        }

                                        if (action === 'rfq') {
                                            handleSubmitRFQ({
                                                markAsCompleted: true,
                                                force_all_to_review: true,
                                            });
                                        }
                                    })
                                    .catch(errorInfo => {
                                        form.scrollToField(errorInfo.errorFields?.[0]?.name);
                                        notification.error({
                                            key: 'form-errors',
                                            duration: 3,
                                            message: 'Please input all required fields',
                                            description: <FormErrorParser isAntd4 errors={errorInfo.errorFields} />,
                                            placement: 'topRight',
                                            maxCount: 1,
                                        });
                                    });
                            }}
                            hasMixedQuoteItems={hasMixedQuoteItems}
                            buyNowCount={getQuoteActionTotals?.buy_now}
                            manualQuoteCount={getQuoteActionTotals?.manual_quote}
                            onSaveDraft={onSaveDraft}
                            onGetHelp={onGetHelp}
                            isStaffUser={isStaffUser}
                            isUserLoggedIn={isUserLoggedIn}
                        />
                    </div>
                </Form>
            </Card>
        </>
    );
};

export default observer(QuoteSideBar);
